"use client";

import { useEffect, useMemo, useState, type ReactNode } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import {
  Controller,
  useForm,
  useWatch,
  type Control,
  type FieldErrors,
  type FieldValues,
  type UseFormRegister,
} from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z, type ZodTypeAny } from "zod";
import {
  Field,
  Input,
  PhoneInput,
  Textarea,
  FormStatus,
  Select,
  SubmitButton,
} from "@/components/ui/Form";
import { Icon, type IconName } from "@/lib/icons/Icon";
import { TabHeader } from "./TabHeader";
import {
  didShowField,
  isFieldRequired,
  type UserProfile,
  type UserProfileUpdate,
  type FieldVisibility,
} from "@/lib/api/user-profile";
import { useDictionary, type DictionaryType } from "@/lib/api/dictionary";
import styles from "./CUserAccounts.module.css";

/**
 * ProfileTabPersonal — "Twoje dane" panel z **sub-zakładkami** per sekcja.
 *
 * **Sub-tabs (pokazywane tylko gdy odpowiedni `fields_config.show_*_tab=true`
 * AND ≥1 pole sekcji ma value `'1'`/`'2'`):**
 *   - **Podstawowe** — zawsze widoczna; email (read-only) + (iter 2e: avatar)
 *   - **Dane personalne** — gated `show_personal_tab`
 *   - **Dane firmowe** — gated `show_company_tab`
 *   - **Dane do faktury** — gated `show_billing_tab`
 *
 * **Form state:** RHF + dynamic Zod schema (zob. `buildSchema`). Schema
 * uwzględnia per-pole walidatory (URL/tel/email/date) ORAZ required gating
 * z `fields_config`. Server-side errors mapowane per-pole przez
 * `setError(name, {type: 'server'})` gdy backend zwróci `errors: {...}` (code 1010).
 *
 * **Save flow:** JEDEN form state dla wszystkich pól ze wszystkich sub-tabs.
 * JEDEN submit button na dole. Wysyła wszystkie zmiany w jednym PATCH do
 * `IAPI_userUpdate` (które filtruje przez whitelist `IFILTER::filterVars`).
 *
 * **Source backend:** dataForm w `c_user_accounts.php` linie 2069-2462.
 */
export function ProfileTabPersonal({
  profile,
  onSaved,
}: {
  profile: UserProfile;
  onSaved: () => void;
}) {
  const fc = profile.fields_config;

  /* Count widocznych pól per sekcja — sub-tab pokazujemy TYLKO gdy:
     1. `show_*_tab === true` (flag z fields_config)
     2. AND choć jedno pole z sekcji ma `'1'` lub `'2'` (didShowField=true) */
  const personalVisible = countVisibleFields(fc?.personal, PERSONAL_FIELDS);
  const companyVisible = countVisibleFields(fc?.company, COMPANY_FIELDS);
  const billingVisible = countVisibleFields(fc?.billing, BILLING_FIELDS);

  /* Basic sub-tab (email read-only) PRZENIESIONA do "Konto" tab — tutaj zostają
     tylko 3 editable sekcje. Gdy żadna nie ma pól -> fallback message
     "administrator nie udostępnił..." (zob. `hasEditableData` poniżej). */
  const tabs: SubTab[] = [
    {
      key: "personal",
      label: "Dane personalne",
      visible: fc?.show_personal_tab === true && personalVisible > 0,
    },
    {
      key: "company",
      label: "Dane firmowe",
      visible: fc?.show_company_tab === true && companyVisible > 0,
    },
    {
      key: "billing",
      label: "Dane do faktury",
      visible: fc?.show_billing_tab === true && billingVisible > 0,
    },
  ];
  const visibleTabs = tabs.filter((t) => t.visible);

  /* hasEditableData = czy admin udostępnił choć jedno pole w którejś sekcji.
     False -> cały panel pokazuje tylko empty-state message (no Save button). */
  const hasEditableData = personalVisible + companyVisible + billingVisible > 0;

  /* Persistent sub-tab przez URL `?subtab=...`. F5 -> URL preserved -> wraca na
     tę samą sub-zakładkę. Również shareable link do konkretnej sekcji. */
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const subtabParam = searchParams?.get("subtab") as SubTabKey | null;
  const isValidSubtab =
    subtabParam !== null &&
    visibleTabs.some((t) => t.key === subtabParam);

  /* Default: pierwsza widoczna sub-tab (personal/company/billing). Gdy żadna
     widoczna -> fallback "personal" jako klucz, ale render i tak pominie content
     (visibleTabs.length === 0 -> tylko empty message). */
  const [activeSubTab, setActiveSubTabState] = useState<SubTabKey>(
    isValidSubtab && subtabParam ? subtabParam : (visibleTabs[0]?.key ?? "personal"),
  );

  /* Sync state ze zmianą URL (np. browser back/forward, hash navigation). */
  useEffect(() => {
    if (isValidSubtab && subtabParam && subtabParam !== activeSubTab) {
      setActiveSubTabState(subtabParam);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subtabParam]);

  /** Update active sub-tab + URL (`replace` żeby nie spam'ować history). */
  const setActiveSubTab = (key: SubTabKey) => {
    setActiveSubTabState(key);
    const params = new URLSearchParams(searchParams?.toString() ?? "");
    params.set("subtab", key);
    /* `{ scroll: false }` — nie scrolluj na top przy zmianie sub-tab'a. */
    router.replace(`${pathname}?${params.toString()}${window.location.hash}`, {
      scroll: false,
    });
  };

  /* Dynamic schema budowany per fields_config — required pola walidowane
     wg `isFieldRequired`, type validators per `FieldDef.type` (url/tel/email/date). */
  const schema = useMemo(() => buildSchema(fc), [fc]);

  const defaultValues = useMemo<FieldValues>(
    () => initFormFromProfile(profile),
    [profile],
  );

  const {
    register,
    handleSubmit,
    control,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<FieldValues>({
    resolver: zodResolver(schema),
    mode: "onTouched",
    defaultValues,
  });

  const [topError, setTopError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const onSubmit = async (values: FieldValues) => {
    setTopError(null);
    setSuccess(null);

    /* Filtrujemy do wysłania TYLKO pola WIDOCZNE w current fields_config.
       Backend i tak filtruje przez `IFILTER::filterVars`, ale UX: nie wysyłamy
       pól których user nie miał szansy edytować. */
    const payload: Record<string, unknown> = {};
    for (const [section, fieldsInSection] of Object.entries(SECTION_FIELDS) as Array<[
      keyof typeof SECTION_FIELDS, FieldDef[]
    ]>) {
      const cfg = (fc as unknown as Record<string, Record<string, FieldVisibility> | undefined>)[section];
      for (const def of fieldsInSection) {
        const cfgKey = def.configKey ?? (def.name as string);
        if (didShowField(cfg, cfgKey)) {
          payload[def.name as string] = values[def.name as string];
        }
      }
    }

    try {
      const res = await fetch("/api/iiapi/user/update", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user: payload }),
      });
      const body = (await res.json().catch(() => null)) as {
        ok?: boolean;
        message?: string;
        errors?: Record<string, string>;
      } | null;

      if (!res.ok) {
        if (body?.errors && Object.keys(body.errors).length > 0) {
          for (const [name, msg] of Object.entries(body.errors)) {
            if (typeof msg === "string") {
              setError(name, { type: "server", message: msg });
            }
          }
          /* Switch na sub-tab z pierwszym błędem — żeby user widział gdzie
             backend rzucił błąd (server-side validation może wskazać pole
             na ukrytej obecnie zakładce). */
          const firstFieldName = Object.keys(body.errors)[0];
          if (firstFieldName) jumpToSubTabFor(firstFieldName);
          /* `||` (nie `??`) — pusty string z backendu też triggeruje fallback. */
          setTopError(body?.message || "Popraw zaznaczone pola formularza.");
          return;
        }
        /* Fallback wg HTTP status — gdy backend nie zwrócił message lub
           zwrócił pusty, user dostaje sensowny komunikat zamiast pustego
           czerwonego banner'a. */
        const fallback =
          res.status === 401 ? "Sesja wygasła — zaloguj się ponownie." :
          res.status === 403 ? "Brak uprawnień do zapisu profilu." :
          res.status === 404 ? "Nie znaleziono profilu użytkownika." :
          res.status >= 500 ? "Błąd serwera. Spróbuj ponownie za chwilę." :
          "Nie udało się zapisać zmian.";
        setTopError(body?.message || fallback);
        return;
      }

      setSuccess(body?.message || "Zmiany zapisane.");
      onSaved();
    } catch (err) {
      setTopError(
        err instanceof Error
          ? `Błąd sieci: ${err.message}`
          : "Błąd sieci podczas zapisu.",
      );
    }
  };

  /**
   * Walidacja client-side nie przeszła — handleSubmit przerywa BEZ wywołania
   * `onSubmit`. Bez tego handler'a user nie wie czemu nic się nie dzieje
   * (błąd może być na ukrytej obecnie sub-zakładce). Przełączamy na sub-tab
   * z pierwszym błędem + pokazujemy top banner.
   */
  const onValidationError = (errs: FieldErrors<FieldValues>) => {
    setSuccess(null);
    const firstFieldName = Object.keys(errs)[0];
    if (firstFieldName) jumpToSubTabFor(firstFieldName);
    setTopError("Sprawdź zaznaczone pola formularza.");
  };

  /** Mapa pole -> sub-tab; switch active sub-tab gdy field jest na innej. */
  const jumpToSubTabFor = (fieldName: string) => {
    const targetTab = FIELD_TO_SUBTAB[fieldName];
    if (targetTab && targetTab !== activeSubTab) {
      setActiveSubTab(targetTab);
    }
  };

  return (
    <>
      <TabHeader icon="user" title="Twoje dane" />
      <form className={styles.profileForm} onSubmit={handleSubmit(onSubmit, onValidationError)} noValidate>

      {/* Sub-tabs nav — visible tylko gdy więcej niż 1 sub-tab */}
      {visibleTabs.length > 1 && (
        <div className={styles.profileSubTabs} role="tablist">
          {visibleTabs.map((t) => (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={activeSubTab === t.key}
              className={activeSubTab === t.key ? styles.profileSubTabActive : styles.profileSubTab}
              onClick={() => setActiveSubTab(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>
      )}

      {/* Active sub-tab content */}
      <div className={styles.profileSubTabContent}>
        {activeSubTab === "personal" && (
          <FieldsRenderer
            sectionConfig={fc?.personal}
            fields={PERSONAL_FIELDS}
            register={register}
            control={control}
            errors={errors}
            busy={isSubmitting}
          />
        )}
        {activeSubTab === "company" && (
          <FieldsRenderer
            sectionConfig={fc?.company}
            fields={COMPANY_FIELDS}
            register={register}
            control={control}
            errors={errors}
            busy={isSubmitting}
          />
        )}
        {activeSubTab === "billing" && (
          <FieldsRenderer
            sectionConfig={fc?.billing}
            fields={BILLING_FIELDS}
            register={register}
            control={control}
            errors={errors}
            busy={isSubmitting}
          />
        )}
      </div>

      {!hasEditableData && (
        <p className={styles.profileEmptyHint}>
          Administrator nie udostępnił dodatkowych danych do uzupełnienia w
          formularzu.
        </p>
      )}

      {topError && <FormStatus variant="error">{topError}</FormStatus>}
      {success && <FormStatus variant="success">{success}</FormStatus>}

      {hasEditableData && (
        <div className={styles.profileFormActions}>
          <SubmitButton loading={isSubmitting}>Zapisz zmiany</SubmitButton>
        </div>
      )}
      </form>
    </>
  );
}

/* ─── Sub-tabs metadata ──────────────────────────────────────────────── */

type SubTabKey = "personal" | "company" | "billing";
type SubTab = { key: SubTabKey; label: string; visible: boolean };

/* ─── Per-section field definitions ──────────────────────────────────── */

/**
 * Pole definition do dynamic render.
 *   - `name` — backend field key (whitelist `IFILTER::filterVars`), używane też
 *     jako klucz w fields_config (chyba że nadpisane przez `configKey`)
 *   - `configKey` — alias dla fields_config (gdy fields_config używa innego
 *     klucza niż database field). Przykład: `about_me` (config) -> `about_me_pl`
 *     (database, per-locale). Default = `name`.
 *   - `label` — display label
 *   - `type` — input type (text/tel/date/url/email/select/textarea/phone/dictionary)
 *   - `autoComplete` — browser autofill hint
 *   - `pair` — gdy pole należy do row pair (np. `name`+`surname` w jednym row)
 *   - `options` — dla `select` (hardcoded opcje, np. sex M/F)
 *   - `icon` — Phosphor icon przed polem (Input `prefix`).
 *   - `dictionary` — symbol słownika (`countries`/`provinces`/`languages`...)
 *     z `IIAPI_getPublicDictionary`. Pole renderowane jako Select z dynamic
 *     options pobranymi z REST API. Storage: `id` z słownika (np. `"PL"`).
 *   - `dictionaryDependsOnField` — nazwa innego pola którego wartość
 *     determinuje opcje (np. `province` zależy od `country`). Gdy parent
 *     field jest empty, Select disabled z hintem.
 */
type FieldDef = {
  name: keyof UserProfileUpdate;
  configKey?: string;
  label: string;
  type?: "text" | "tel" | "date" | "url" | "email" | "select" | "textarea" | "phone" | "dictionary" | "bank";
  autoComplete?: string;
  pair?: string;
  options?: Array<{ value: string; label: string }>;
  icon?: IconName;
  dictionary?: DictionaryType;
  dictionaryDependsOnField?: keyof UserProfileUpdate;
};

const PERSONAL_FIELDS: FieldDef[] = [
  { name: "name", label: "Imię", autoComplete: "given-name", pair: "name-row" },
  { name: "surname", label: "Nazwisko", autoComplete: "family-name", pair: "name-row" },
  {
    name: "sex",
    label: "Płeć",
    type: "dictionary",
    dictionary: "sex",
    pair: "personal-meta",
  },
  { name: "birth_date", label: "Data urodzenia", type: "date", autoComplete: "bday", pair: "personal-meta" },
  { name: "pesel", label: "PESEL", pair: "id-row" },
  { name: "nr_id_card", label: "Nr dowodu", pair: "id-row" },
  { name: "telephone", label: "Telefon", type: "phone", autoComplete: "tel" },
  { name: "www", label: "Strona WWW", type: "url", autoComplete: "url", icon: "link" },
  {
    name: "country",
    label: "Kraj",
    type: "dictionary",
    dictionary: "countries",
    autoComplete: "country",
    pair: "address-1",
  },
  { name: "city", label: "Miasto", autoComplete: "address-level2", pair: "address-1" },
  { name: "street", label: "Ulica", autoComplete: "address-line1", pair: "address-2" },
  { name: "nr", label: "Nr", autoComplete: "address-line2", pair: "address-2" },
  { name: "post_code", label: "Kod pocztowy", autoComplete: "postal-code", pair: "address-3" },
  {
    name: "province",
    label: "Województwo",
    type: "dictionary",
    dictionary: "provinces",
    dictionaryDependsOnField: "country",
    autoComplete: "address-level1",
    pair: "address-3",
  },
  { name: "state", label: "Stan/Region", pair: "address-4" },
  { name: "nip", label: "NIP", pair: "fiscal-row" },
  { name: "bank_account_number", label: "Numer konta bankowego", type: "bank", pair: "fiscal-row" },
  /* `about_me` w fields_config odnosi się do 3 locale variants. Renderujemy
     `about_me_pl` (current PL locale) — iter później dorzucimy switcher locale
     / multi-textarea gdy domena ma wiele języków. */
  { name: "about_me_pl", configKey: "about_me", label: "O mnie", type: "textarea" },
  { name: "facebook_account_url", label: "Facebook", type: "url", autoComplete: "off", icon: "facebook-logo" },
  { name: "twitter_account_url", label: "Twitter / X", type: "url", autoComplete: "off", icon: "twitter-logo" },
  { name: "linkedin_account_url", label: "LinkedIn", type: "url", autoComplete: "off", icon: "linkedin-logo" },
  { name: "youtube_account_url", label: "YouTube", type: "url", autoComplete: "off", icon: "youtube-logo" },
  { name: "pinterest_account_url", label: "Pinterest", type: "url", autoComplete: "off", icon: "pinterest-logo" },
];

/**
 * Sekcja company w `required_fields_config` zawiera 5 pól CRM-only, których
 * UI istnieje tylko w module `m_crm_contractors` (kartoteka kontrahenta):
 * `company_language`, `company_related_buyer`, `jst`, `gv`, `idwew`.
 * Te pola NIE są listowane tutaj — c_user_accounts (frontend konta) ich nie
 * renderuje, IAPI_userUpdate whitelist ich nie akceptuje, a helper PHP
 * CRM_ONLY_COMPANY_FIELDS wyklucza je z walidacji konta użytkownika.
 * Zob. ProfileTabs.tsx `excludedCompany` w warnUnhandledFields.
 */
const COMPANY_FIELDS: FieldDef[] = [
  { name: "company_name", label: "Nazwa firmy" },
  { name: "company_nip", label: "NIP", pair: "company-id" },
  { name: "company_krs", label: "KRS", pair: "company-id" },
  { name: "regon", label: "REGON", pair: "company-id" },
  {
    name: "company_country",
    label: "Kraj",
    type: "dictionary",
    dictionary: "countries",
    autoComplete: "country",
    pair: "company-addr-1",
  },
  { name: "company_city", label: "Miasto", pair: "company-addr-1" },
  { name: "company_street", label: "Ulica", pair: "company-addr-2" },
  { name: "company_nr", label: "Nr", pair: "company-addr-2" },
  { name: "company_post_code", label: "Kod pocztowy", pair: "company-addr-3" },
  {
    name: "company_province",
    label: "Województwo",
    type: "dictionary",
    dictionary: "provinces",
    dictionaryDependsOnField: "company_country",
    pair: "company-addr-3",
  },
  { name: "company_state", label: "Stan/Region", pair: "company-addr-4" },
  { name: "company_telephone", label: "Telefon", type: "phone", pair: "company-contact" },
  { name: "company_fax", label: "Fax", type: "phone", pair: "company-contact" },
  { name: "company_www", label: "Strona WWW", type: "url", icon: "link" },
  { name: "company_contact_person_name", label: "Osoba kontaktowa — imię", pair: "company-person" },
  { name: "company_contact_person_surname", label: "Osoba kontaktowa — nazwisko", pair: "company-person" },
  { name: "company_trade", label: "Branża" },
  { name: "company_bank_account_number", label: "Numer konta bankowego", type: "bank" },
];

const BILLING_FIELDS: FieldDef[] = [
  { name: "billing_name", label: "Nazwa do faktury" },
  { name: "billing_nip", label: "NIP" },
  {
    name: "billing_country",
    label: "Kraj",
    type: "dictionary",
    dictionary: "countries",
    autoComplete: "country",
    pair: "billing-addr-1",
  },
  { name: "billing_city", label: "Miasto", pair: "billing-addr-1" },
  { name: "billing_street", label: "Ulica", pair: "billing-addr-2" },
  { name: "billing_nr", label: "Nr", pair: "billing-addr-2" },
  { name: "billing_post_code", label: "Kod pocztowy" },
];

/**
 * Map sekcja -> lista pól def (z FieldDef do iteracji w buildSchema + payload).
 */
const SECTION_FIELDS = {
  personal: PERSONAL_FIELDS,
  company: COMPANY_FIELDS,
  billing: BILLING_FIELDS,
} as const;

/**
 * Reverse lookup: field name -> sub-tab key. Używane gdy validation błąd
 * pada na ukrytej obecnie sub-zakładce -> przełączamy active tab żeby user
 * widział pole z błędem (zamiast widzieć top banner bez kontekstu).
 */
const FIELD_TO_SUBTAB: Record<string, SubTabKey> = (() => {
  const map: Record<string, SubTabKey> = {};
  for (const f of PERSONAL_FIELDS) map[f.name as string] = "personal";
  for (const f of COMPANY_FIELDS) map[f.name as string] = "company";
  for (const f of BILLING_FIELDS) map[f.name as string] = "billing";
  return map;
})();

/* ─── Schema builder ──────────────────────────────────────────────────── */

/**
 * Buduje Zod schema DYNAMICZNIE z `fields_config`. Per pole:
 *   - jeśli HIDDEN (`didShowField=false`) -> schema pomija pole (RHF traktuje
 *     jako optional any-string z defaultu)
 *   - jeśli REQUIRED (`isFieldRequired=true`) -> `.min(1, "...")` + type validator
 *   - jeśli OPTIONAL -> type validator + `.literal("")` fallback (pusty = OK)
 *
 * Type validators (analogia do CContactForm):
 *   - `url` -> `^https?://` regex
 *   - `tel` / `phone` -> E.164 pattern (`^\+\d{6,15}$`) — backend normalizuje przez
 *     `IFILTER::phoneE164` więc spodziewamy się E.164 z PhoneInput
 *   - `email` -> standard email
 *   - `date` -> ISO date (YYYY-MM-DD) — wystarczy `z.string()` bo input type="date"
 */
function buildSchema(fc: UserProfile["fields_config"]) {
  const shape: Record<string, ZodTypeAny> = {};

  for (const [section, fields] of Object.entries(SECTION_FIELDS) as Array<[
    keyof typeof SECTION_FIELDS, FieldDef[]
  ]>) {
    const sectionConfig = (fc as unknown as Record<string, Record<string, FieldVisibility> | undefined>)[section];

    for (const def of fields) {
      const cfgKey = def.configKey ?? (def.name as string);
      const visible = didShowField(sectionConfig, cfgKey);
      const required = isFieldRequired(sectionConfig, cfgKey);

      /* Pole niewidoczne — schema je akceptuje jako pusty string (default value),
         payload filter w onSubmit i tak je odetnie. */
      if (!visible) {
        shape[def.name as string] = z.string().optional();
        continue;
      }

      const requiredMsg = `Pole "${def.label}" jest wymagane.`;
      let typeValidator: z.ZodString = z.string();

      if (def.type === "url") {
        typeValidator = typeValidator.regex(
          /^https?:\/\//i,
          "Adres URL musi zaczynać się od http:// lub https://.",
        );
      } else if (def.type === "phone") {
        /* E.164: + i 6-15 cyfr (telefon krajowy +48xxx, międzynarodowy max 15). */
        typeValidator = typeValidator.regex(
          /^\+\d{6,15}$/,
          "Niepoprawny numer telefonu.",
        );
      } else if (def.type === "tel") {
        typeValidator = typeValidator.regex(
          /^[\d +()\-]{6,20}$/,
          "Niepoprawny numer telefonu.",
        );
      } else if (def.type === "email") {
        typeValidator = typeValidator.email("Niepoprawny adres email.");
      }

      if (required) {
        shape[def.name as string] = z
          .string()
          .min(1, requiredMsg)
          .pipe(typeValidator);
      } else {
        shape[def.name as string] = z.union([typeValidator, z.literal("")]);
      }
    }
  }

  /* Object-level cross-field validation — country-aware NIP/post_code/bank.
     Activeguj TYLKO gdy parent country jest set; gdy country="" -> user
     jeszcze nie wybrał kraju, więc nie wiemy jakiego formatu egzekwować
     -> nie waliduj formatu (skip). */
  return z.object(shape).superRefine((data, ctx) => {
    /* Personal address — guard'owane przez `data.country`. */
    validateCountrySpecific(ctx, {
      country: data.country,
      post_code: data.post_code,
      nip: data.nip,
      bank_account_number: data.bank_account_number,
      postCodePath: "post_code",
      nipPath: "nip",
      bankPath: "bank_account_number",
    });

    /* Company address — guard'owane przez `data.company_country`. */
    validateCountrySpecific(ctx, {
      country: data.company_country,
      post_code: data.company_post_code,
      nip: data.company_nip,
      bank_account_number: data.company_bank_account_number,
      postCodePath: "company_post_code",
      nipPath: "company_nip",
      bankPath: "company_bank_account_number",
    });

    /* Billing address — guard'owane przez `data.billing_country`. */
    validateCountrySpecific(ctx, {
      country: data.billing_country,
      post_code: data.billing_post_code,
      nip: data.billing_nip,
      postCodePath: "billing_post_code",
      nipPath: "billing_nip",
    });
  });
}

/**
 * Country-specific format validators (PL: kod pocztowy XX-XXX, NIP 10 cyfr
 * z checksum, bank account 26 cyfr IBAN format).
 *
 * **Skip gdy `country` pusty** — user nie wybrał jeszcze kraju, nie znamy
 * formatu do egzekwowania. Backend i tak akceptuje dowolny string (varchar).
 *
 * Dla krajów innych niż PL — basic non-empty check, bez format checków
 * (TODO: dodać per kraj walidatory gdy będą potrzebne).
 */
function validateCountrySpecific(
  ctx: z.RefinementCtx,
  args: {
    country: unknown;
    post_code?: unknown;
    nip?: unknown;
    bank_account_number?: unknown;
    postCodePath: string;
    nipPath: string;
    bankPath?: string;
  },
) {
  const country = typeof args.country === "string" ? args.country.trim() : "";
  if (!country) return; //user nie wybrał kraju -> skip format checks

  const postCode = typeof args.post_code === "string" ? args.post_code.trim() : "";
  const nip = typeof args.nip === "string" ? args.nip.trim() : "";
  const bank = typeof args.bank_account_number === "string" ? args.bank_account_number.trim() : "";

  if (country === "PL") {
    /* PL kod pocztowy: 00-000 (2 cyfry, dash, 3 cyfry). */
    if (postCode && !/^\d{2}-\d{3}$/.test(postCode)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Format kodu pocztowego dla Polski: XX-XXX (np. 00-001).",
        path: [args.postCodePath],
      });
    }

    /* PL NIP: 10 cyfr z checksum (waga 6,5,7,2,3,4,5,6,7). */
    if (nip) {
      const digits = nip.replace(/[^\d]/g, "");
      if (digits.length !== 10 || !validatePolishNipChecksum(digits)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "NIP musi mieć 10 cyfr i poprawną sumę kontrolną.",
          path: [args.nipPath],
        });
      }
    }

    /* PL numer konta bankowego: 26 cyfr (NRB) lub IBAN `PL` + 26 cyfr (28 znaków). */
    if (args.bankPath && bank) {
      const cleaned = bank.replace(/\s+/g, "").toUpperCase();
      const isNRB = /^\d{26}$/.test(cleaned);
      const isIBAN = /^PL\d{26}$/.test(cleaned);
      if (!isNRB && !isIBAN) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Numer konta: 26 cyfr (NRB) lub PL + 26 cyfr (IBAN).",
          path: [args.bankPath],
        });
      }
    }
  }
  /* Pozostałe kraje — bez format checków (TODO: dodać gdy potrzeba). */
}

/**
 * Walidacja sumy kontrolnej polskiego NIP (10 cyfr).
 * Algorytm: weights = [6,5,7,2,3,4,5,6,7], sum = Σ(wᵢ·dᵢ) dla i=0..8,
 * checksum = sum % 11, walid jeśli checksum === d₉ (10-ta cyfra).
 */
function validatePolishNipChecksum(digits: string): boolean {
  if (digits.length !== 10) return false;
  const weights = [6, 5, 7, 2, 3, 4, 5, 6, 7];
  const sum = weights.reduce(
    (acc, w, i) => acc + w * parseInt(digits[i]!, 10),
    0,
  );
  const checksum = sum % 11;
  /* Edge: checksum === 10 -> NIP nie istnieje (puste/invalid). */
  if (checksum === 10) return false;
  return checksum === parseInt(digits[9]!, 10);
}

/* ─── Sub-section components ─────────────────────────────────────────── */

/**
 * Renderer pól per sekcja — iteruje po `fields` def, sprawdza `didShowField`
 * per pole, grupuje pola z tym samym `pair` w jeden row.
 */
function FieldsRenderer({
  sectionConfig,
  fields,
  register,
  control,
  errors,
  busy,
}: {
  sectionConfig: Record<string, FieldVisibility> | undefined | null;
  fields: FieldDef[];
  register: UseFormRegister<FieldValues>;
  control: Control<FieldValues>;
  errors: FieldErrors<FieldValues>;
  busy: boolean;
}) {
  const visibleFields = fields.filter((f) =>
    didShowField(sectionConfig, f.configKey ?? (f.name as string)),
  );
  if (visibleFields.length === 0) return null;

  /* Group adjacent fields by `pair` key -> render w jednym row. */
  const rows: FieldDef[][] = [];
  let currentRow: FieldDef[] = [];
  let currentPair: string | undefined = undefined;
  for (const field of visibleFields) {
    if (field.pair && field.pair === currentPair) {
      currentRow.push(field);
    } else {
      if (currentRow.length > 0) rows.push(currentRow);
      currentRow = [field];
      currentPair = field.pair;
    }
  }
  if (currentRow.length > 0) rows.push(currentRow);

  return (
    <div className={styles.profileSectionFields}>
      {rows.map((row, idx) => (
        <FieldRow
          key={idx}
          fields={row}
          register={register}
          control={control}
          errors={errors}
          sectionConfig={sectionConfig}
          busy={busy}
        />
      ))}
    </div>
  );
}

function FieldRow({
  fields,
  register,
  control,
  errors,
  sectionConfig,
  busy,
}: {
  fields: FieldDef[];
  register: UseFormRegister<FieldValues>;
  control: Control<FieldValues>;
  errors: FieldErrors<FieldValues>;
  sectionConfig: Record<string, FieldVisibility> | undefined | null;
  busy: boolean;
}) {
  if (fields.length === 1) {
    return (
      <FieldInput
        field={fields[0]!}
        register={register}
        control={control}
        errors={errors}
        sectionConfig={sectionConfig}
        busy={busy}
      />
    );
  }
  return (
    <div className={styles.fieldRow}>
      {fields.map((f) => (
        <FieldInput
          key={String(f.name)}
          field={f}
          register={register}
          control={control}
          errors={errors}
          sectionConfig={sectionConfig}
          busy={busy}
        />
      ))}
    </div>
  );
}

function FieldInput({
  field,
  register,
  control,
  errors,
  sectionConfig,
  busy,
}: {
  field: FieldDef;
  register: UseFormRegister<FieldValues>;
  control: Control<FieldValues>;
  errors: FieldErrors<FieldValues>;
  sectionConfig: Record<string, FieldVisibility> | undefined | null;
  busy: boolean;
}) {
  const cfgKey = field.configKey ?? (field.name as string);
  const required = isFieldRequired(sectionConfig, cfgKey);
  const name = field.name as string;
  const errMessage =
    typeof errors[name]?.message === "string"
      ? (errors[name]?.message as string)
      : undefined;

  let control_: ReactNode;
  if (field.type === "dictionary" && field.dictionary) {
    /* Dictionary-backed Select — opcje pobrane z REST API. Osobny komponent
       żeby `useDictionary` hook był wywoływany na top-level (hooks rules).
       Controller-based (controlled) — preserves preselected value gdy options
       ładują się asynchronicznie. */
    control_ = (
      <DictionaryField
        field={field}
        control={control}
        busy={busy}
      />
    );
  } else if (field.type === "select" && field.options) {
    control_ = (
      <Select disabled={busy} {...register(name)}>
        {field.options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </Select>
    );
  } else if (field.type === "textarea") {
    control_ = <Textarea rows={3} disabled={busy} {...register(name)} />;
  } else if (field.type === "phone") {
    /* PhoneInput — kontrolowany przez RHF Controller (custom value/onChange). */
    control_ = (
      <Controller
        name={name}
        control={control}
        render={({ field: rhfField }) => (
          <PhoneInput
            value={(rhfField.value as string) ?? ""}
            onChange={(v) => rhfField.onChange(v ?? "")}
            disabled={busy}
          />
        )}
      />
    );
  } else if (field.type === "bank") {
    /* BankAccountField — display format "12 3456 7890 ...", storage raw digits. */
    control_ = (
      <BankAccountField name={name} control={control} busy={busy} />
    );
  } else {
    control_ = (
      <Input
        type={field.type ?? "text"}
        autoComplete={field.autoComplete}
        disabled={busy}
        prefix={field.icon ? <Icon name={field.icon} /> : undefined}
        {...register(name)}
      />
    );
  }

  return (
    <Field label={field.label} required={required} error={errMessage}>
      {control_}
    </Field>
  );
}

/**
 * Dictionary-backed field — renderuje Select z opcjami pobranymi z REST API.
 *
 * **Cascade (provinces zależą od country):** gdy `field.dictionaryDependsOnField`
 * jest ustawione, użyj `useWatch` żeby subskrybować zmiany parent field'a
 * — refetch dictionary gdy parent value się zmieni.
 *
 * **Controlled przez RHF Controller:** kluczowe — Select musi być controlled
 * żeby zachować preselected value gdy options ładują się asynchronicznie.
 * Bez Controller (register only) DOM select gubi value="PL" gdy initial render
 * jest z items=[] -> browser wybiera pierwszą opcję ("—") -> po doczytaniu items
 * RHF wartość nadal "PL", ale DOM select pokazuje pustą. Controlled `value=`
 * wymusza re-sync przy każdym re-renderze.
 *
 * **States:**
 *   - Loading -> Select disabled + opcja "Ładowanie…" (pierwszy render)
 *   - Error -> Select disabled + opcja z błędem (defensive)
 *   - Parent empty (cascade) -> Select disabled + "Wybierz najpierw {parent}"
 *   - OK -> opcje z dictionary; pierwsza opcja "—" (empty) gdy non-required
 *
 * **Storage value:** `id` z dictionary (np. `"PL"`, `"US"`). Backend whitelist
 * pól `IAPI_userUpdate` akceptuje string — backend nie waliduje czy to valid
 * country code (dictionary jest UI-side gating).
 */
/**
 * BankAccountField — input z auto-formatem (groups of 4 digits) na display,
 * raw digits w storage. Działa dla PL NRB (26 cyfr) oraz IBAN (PL + 26 cyfr).
 *
 * **Display:** `12 3456 7890 1234 5678 9012 3456` (grupy po 4 z spacjami)
 * lub `PL12 3456 7890 ...` (IBAN).
 *
 * **Storage:** `12345678901234567890123456` (raw digits) lub `PL1234...` (IBAN
 * z prefix bez spacji). Walidator Zod (`validateCountrySpecific`) sprawdza
 * `/^\d{26}$/` lub `/^PL\d{26}$/`.
 *
 * **autoComplete="off"** — zapobiega autofill (browser może próbować wstawić
 * np. zapisany numer karty kredytowej).
 *
 * **inputMode="numeric"** — mobile keyboard z cyframi.
 *
 * **maxLength** — pozwala wpisać do 33 znaków (PL + 26 cyfr + 6 spacji =
 * `PL12 3456 7890 1234 5678 9012 3456` = 33 chars).
 */
function BankAccountField({
  name,
  control,
  busy,
}: {
  name: string;
  control: Control<FieldValues>;
  busy: boolean;
}) {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field: rhfField }) => {
        const raw = (rhfField.value as string) ?? "";
        const displayValue = formatBankAccountDisplay(raw);
        return (
          <Input
            type="text"
            value={displayValue}
            onChange={(e) => {
              /* Strip spaces przed zapisem do form state — storage raw. */
              const stripped = e.target.value.replace(/\s+/g, "").toUpperCase();
              rhfField.onChange(stripped);
            }}
            onBlur={rhfField.onBlur}
            name={rhfField.name}
            ref={rhfField.ref}
            disabled={busy}
            autoComplete="off"
            inputMode="numeric"
            maxLength={33}
          />
        );
      }}
    />
  );
}

/**
 * Format display: `12345678901234567890123456` -> `12 3456 7890 1234 5678 9012 3456`.
 * IBAN: `PL12345...` -> `PL12 3456 ...`.
 *
 * Algorithm PL bank:
 *   - 2-char prefix (kraj/sumy kontrolnej) lub `PL` IBAN -> osobno
 *   - reszta: grupy po 4 cyfry
 *
 * Bez prefix'a: zacznij od pierwszych 2 cyfr (suma kontrolna NRB), potem 4-tki.
 */
function formatBankAccountDisplay(raw: string): string {
  if (!raw) return "";
  const cleaned = raw.replace(/\s+/g, "");

  /* IBAN format `PL\d{26}` -> `PL12 3456 7890 ...` */
  const ibanMatch = cleaned.match(/^([A-Z]{2})(\d+)$/);
  if (ibanMatch) {
    const [, prefix, digits] = ibanMatch;
    return prefix + groupDigits(digits!, 4, " ");
  }

  /* NRB format: czyste cyfry. 2 pierwsze (suma kontrolna) + grupy po 4. */
  if (/^\d+$/.test(cleaned) && cleaned.length >= 3) {
    return cleaned.slice(0, 2) + " " + groupDigits(cleaned.slice(2), 4, " ");
  }

  /* Fallback (user wpisuje, np. częściowo) — bez format. */
  return cleaned;
}

/** Helper — pogrupuj string w bloki po `size` z separatorem `sep`. */
function groupDigits(digits: string, size: number, sep: string): string {
  return digits.match(new RegExp(`.{1,${size}}`, "g"))?.join(sep) ?? digits;
}

function DictionaryField({
  field,
  control,
  busy,
}: {
  field: FieldDef;
  control: Control<FieldValues>;
  busy: boolean;
}) {
  const name = field.name as string;

  /* Subscribe to parent field value (np. country dla province). */
  const parentName = field.dictionaryDependsOnField as string | undefined;
  const parentValue = useWatch({
    control,
    name: parentName ?? "__noop__",
  });
  const parentString = typeof parentValue === "string" ? parentValue : "";

  const { items, loading, error } = useDictionary(field.dictionary!, {
    country: parentString || undefined,
  });

  /* Disabled gdy: form busy, dictionary loading, OR cascade-parent empty. */
  const cascadeBlocked = !!parentName && !parentString;
  const isDisabled = busy || loading || cascadeBlocked || !!error;

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: rhfField }) => (
        <Select
          disabled={isDisabled}
          value={(rhfField.value as string) ?? ""}
          onChange={(e) => rhfField.onChange(e.target.value)}
          onBlur={rhfField.onBlur}
          name={rhfField.name}
          ref={rhfField.ref}
        >
          <option value="">
            {cascadeBlocked
              ? `Wybierz najpierw ${parentName === "country" || parentName === "company_country" ? "kraj" : "pole nadrzędne"}`
              : loading
                ? "Ładowanie…"
                : error
                  ? "Błąd ładowania słownika"
                  : "—"}
          </option>
          {items.map((opt) => (
            <option key={opt.id} value={opt.id}>
              {opt.name}
            </option>
          ))}
        </Select>
      )}
    />
  );
}

/* ─── Helpers ────────────────────────────────────────────────────────── */

/**
 * Liczy ile pól z `fields` def ma value `'1'/'2'` w `sectionConfig`.
 * Używa `configKey ?? name` — pozwala na alias mapping (np. `about_me_pl`
 * field z aliasem `about_me` w config).
 */
function countVisibleFields(
  sectionConfig: Record<string, FieldVisibility> | undefined | null,
  fields: FieldDef[],
): number {
  if (!sectionConfig) return 0;
  return fields.filter((f) => didShowField(sectionConfig, f.configKey ?? (f.name as string))).length;
}

/**
 * Initialize form state from profile data — wszystkie whitelist'owane pola
 * pre-populated wartością z `profile.*` (lub pusty string).
 */
function initFormFromProfile(profile: UserProfile): FieldValues {
  const allFields: FieldDef[] = [
    ...PERSONAL_FIELDS,
    ...COMPANY_FIELDS,
    ...BILLING_FIELDS,
  ];
  const form: Record<string, string> = {};
  for (const f of allFields) {
    const key = f.name as string;
    const v = (profile as Record<string, unknown>)[key];
    form[key] = typeof v === "string" ? v : v == null ? "" : String(v);
  }
  return form;
}
